import { useEffect, useState } from "react";
import { Modal } from "./Modal";
import { Button } from "./Button";
import { Field } from "./Field";

/**
 * Ask for one short piece of text (#328).
 *
 * Replaces window.prompt, which was creating REAL RECORDS in two places
 * -- a honey varietal and a hive issue. A browser prompt can be
 * suppressed by the user ("prevent this page from creating additional
 * dialogs"), after which it returns null silently and the action simply
 * stops working with no error and nothing on screen.
 *
 * It also cannot enforce a maximum length, cannot show a hint, cannot be
 * themed, and reads like a browser error on mobile web. For a field that
 * ends up in the database with a column width, that last one is not
 * cosmetic: the API would reject an over-long value only after the user
 * had typed it and pressed OK.
 *
 * Kept deliberately small. Anything needing two fields is a Modal with a
 * form, not this.
 */
export function PromptDialog({
  open, onClose, onSubmit, title, label, hint, initialValue = "",
  placeholder, confirmLabel = "Save", maxLength = 100, busy = false,
}) {
  const [value, setValue] = useState("");

  /*
   * Reset on open, not on close: leaving the old text in place would
   * offer the last answer as the default for a different question.
   *
   * initialValue is for RENAMING, where the current name is the right
   * starting point -- fixing "Clovr" should be one keystroke, not
   * retyping the word. Mobile's rename sheet already opened holding the
   * name; web's opened empty, so the same gesture cost more on the
   * platform with the keyboard. Defaults to "" so every other caller is
   * unchanged.
   */
  useEffect(() => {
    if (open) setValue(initialValue);
  }, [open, initialValue]);

  const trimmed = value.trim();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={() => onSubmit(trimmed)} disabled={busy || !trimmed}>
            {busy ? "Working…" : confirmLabel}
          </Button>
        </>
      }
    >
      <Field label={label} hint={hint}>
        {(id) => (
          <input
            id={id}
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            // Enter submits, because this replaced a control where it did.
            onKeyDown={(e) => {
              if (e.key === "Enter" && trimmed && !busy) onSubmit(trimmed);
            }}
            placeholder={placeholder}
            maxLength={maxLength}
            className="touch-target"
            style={{ width: "100%" }}
          />
        )}
      </Field>
    </Modal>
  );
}
